import React, {forwardRef} from 'react';

import {useModal} from '../ModalProvider';
import {ModalTitle} from '../ModalTitle';
import {ModalHeaderView, ModalHeaderContentView} from './ModalHeader.styles';

import type {HTMLAttributes, SpringTooling} from '../../__SharedTypes';
import {ArrowLeftIcon} from '../../../icons';
import IconButton from '../../IconButton';

type ModalHeaderProps = {
  /**
   * Contents of the Modal header.
   */
  children?: React.ReactNode;
  /**
   * id used for Jest and Cypress test identification.
   */
  ['data-automation-id']?: string;
  /**
   * Whether or not a back arrow icon button appears in the top-left corner of the Modal header.
   */
  hasBackButton?: boolean;
  /**
   * Whether the ModalHeader should have a border.
   */
  hasBorder?: boolean;
  /**
   * Callback for when the back button is clicked. Used with the hasBackButton prop.
   */
  onBackButtonClick?: (event: React.MouseEvent) => void;
  /**
   * The title of the Modal header.
   */
  title?: string;
} & HTMLAttributes &
  SpringTooling;

export const ModalHeader = forwardRef<HTMLDivElement, ModalHeaderProps>(
  (
    {
      children,
      title,
      hasBackButton,
      hasBorder = true,
      onBackButtonClick,
      ...props
    },
    ref
  ) => {
    const {hasCloseButton, setHasRenderedHeader} = useModal();

    React.useEffect(() => {
      setHasRenderedHeader(true);
      return () => {
        setHasRenderedHeader(false);
      };
    }, [setHasRenderedHeader]);

    return (
      <ModalHeaderView
        {...props}
        data-modal-header
        hasBorder={hasBorder}
        ref={ref}
      >
        {hasBackButton ? (
          <IconButton
            Icon={ArrowLeftIcon}
            label="Back"
            onClick={onBackButtonClick}
            variant="ghost"
          />
        ) : null}
        <ModalHeaderContentView
          hasCloseButton={hasCloseButton}
          justify="space-between"
        >
          {title ? <ModalTitle>{title}</ModalTitle> : null}
          {children}
        </ModalHeaderContentView>
      </ModalHeaderView>
    );
  }
);

ModalHeader.displayName = 'ModalHeader';
ModalHeader.toString = ModalHeaderView.toString;

export default ModalHeader;
